import { useQuery } from "@tanstack/react-query";
import { queryClient } from "~/app/queryClient";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { useCommerceContext } from "~/app/config/useSession";
import type {
  CustomerPerformanceDashboardData,
  CustomerPerformanceProgressData,
} from "~/features/cms/homepagePersonalization/cpd/CPDTypes";
import type { SelectedAccountData } from "~/domains/user/types/SessionUserData";

export const customerPerformanceKeys = {
  all: ["customer-performance"] as const,
  dashboard: (accountKey: string) => ["customer-performance", "dashboard", accountKey] as const,
  progress: (accountKey: string) => ["customer-performance", "progress", accountKey] as const,
};

const fetchCustomerPerformance = async <T>(path: string): Promise<T | null> => {
  const response = await occFetch(occUrl(path), {}, { accountContext: "omit" });
  if (response.status === 204 || response.status === 403 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const error = new Error(`Customer Performance fetch failed: ${response.status}`);
    (error as Error & { status: number }).status = response.status;
    throw error;
  }
  return response.json();
};

const accountKey = (account: SelectedAccountData | null | undefined) =>
  account ? [account.uid ?? "", account.salesOrg ?? "", account.endCustomerCode ?? ""].join("|") : "";

export function useCustomerPerformanceDashboard() {
  const account = useCommerceContext()?.selectedAccount;
  const key = accountKey(account);
  return useQuery({
    queryKey: customerPerformanceKeys.dashboard(key),
    queryFn: () => fetchCustomerPerformance<CustomerPerformanceDashboardData>("/customer-performance/dashboard"),
    enabled: Boolean(key),
  });
}

export function useCustomerPerformanceProgress() {
  const account = useCommerceContext()?.selectedAccount;
  const key = accountKey(account);
  return useQuery({
    queryKey: customerPerformanceKeys.progress(key),
    queryFn: () => fetchCustomerPerformance<CustomerPerformanceProgressData>("/customer-performance/progress"),
    enabled: Boolean(key),
  });
}

export function invalidateCustomerPerformanceDashboard(): void {
  void queryClient.invalidateQueries({ queryKey: customerPerformanceKeys.all });
}
